import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, Eye, TrendingUp, ShieldCheck, Award, ArrowRight } from 'lucide-react';
import { heroImages, stats } from '../mock';
import Seo from '../components/Seo';

const pillars = [
  { icon: Users, title: 'Verified Professional Tipsters', desc: 'Our service is powered by 10 verified professional tipsters, each with years of proven experience in football analytics, betting strategy, odds evaluation and statistical modelling.' },
  { icon: Target, title: 'Daily Premium Picks', desc: 'Every selection is backed by deep data and expert evaluation. We only release a tip when the value and confidence are genuinely optimal.' },
  { icon: Eye, title: 'Transparent Results', desc: 'We publish every result openly — wins and losses alike. No cherry-picking, no hidden history. What you see in our archive is exactly what we sent.' },
  { icon: TrendingUp, title: 'Disciplined Strategy', desc: 'Our mission is to maximise long-term profit while minimising unnecessary risk through disciplined, value-driven betting.' },
];

export default function About() {
  return (
    <div>
      <Seo
        title="About Us | Verified Football Tipster Team – 24BetBox"
        description="24BetBox is powered by 10 verified professional football tipsters. Learn about our data-driven, disciplined approach and our commitment to transparent results."
        path="/about"
      />
      <section className="hero-gradient border-b border-[#2e2822]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">24betbox.com</p>
          <h1 className="mt-2 font-heading text-5xl font-extrabold text-white sm:text-6xl">About Us</h1>
          <p className="mt-4 max-w-2xl text-neutral-300">The home of elite sports predictions crafted for serious bettors who demand accuracy, discipline and real results.</p>
        </div>
      </section>

      <section className="bg-[#15120f] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="font-heading text-4xl font-extrabold text-white">24BETBOX VIP Premium Tips</h2>
              <div className="mt-5 space-y-4 text-neutral-300">
                <p>Welcome to 24betbox.com, the home of elite football predictions built for bettors who take sports investing seriously. Our VIP Premium Tips service is powered by a dedicated team of 10 verified professional tipsters, each with years of hands-on experience in football analytics and betting strategy.</p>
                <p>Every single day, our team commits over 10 hours of intensive research — reviewing form, team news, tactical matchups, market movements, injury updates and dozens of other factors that influence real match outcomes. We don't rely on luck. We rely on knowledge, experience and expertise.</p>
                <p>Our mission is simple: to provide reliable, high-quality predictions with the goal of maximising long-term profit while minimising unnecessary risk.</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-[#2e2822] shadow-2xl">
              <img src={heroImages.crowd} alt="Stadium crowd" className="h-[380px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#15120f] via-transparent to-transparent" />
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 gap-6 rounded-2xl border border-[#2e2822] bg-[#1b1712] p-8 md:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <p className="font-heading text-4xl font-extrabold orange-text-gradient">{s.value}</p>
                <p className="mt-1 text-sm text-neutral-400">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <h2 className="text-center font-heading text-4xl font-extrabold text-white">What Makes Us Different</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="card-dark rounded-xl p-7">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/15"><Icon className="h-6 w-6 text-orange-500" /></div>
                    <h3 className="mt-4 font-heading text-xl font-bold text-white">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">{p.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-16 flex flex-col items-center rounded-2xl border border-[#2e2822] bg-[#1b1712] p-10 text-center">
            <Award className="h-12 w-12 text-orange-500" />
            <h3 className="mt-4 font-heading text-3xl font-extrabold text-white">Integrity & Precision First</h3>
            <p className="mt-3 max-w-2xl text-neutral-300">At 24BETBOX, we select only the strongest opportunities — no forced bets, no inflated promises. Our community trusts us because we deliver consistency, stability and a premium betting experience.</p>
            <Link to="/contact" className="btn-orange mt-7 inline-flex items-center gap-2 rounded-lg px-8 py-3.5 font-heading text-base font-bold uppercase tracking-wide">Join VIP Now <ArrowRight className="h-5 w-5" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
